import { createSelector } from 'reselect';
import _ from 'lodash';
import ExperienceSelectors from '../experience/experience-selectors';

const _scopedState = (state) => state.entities.winner;

// *** INPUT SELECTORS *** //
const _drawsById = (state) => _scopedState(state).winners.drawsById;
// note that this will select all winners, even if they've been archived i.e. swapped out in a draw
const _winnersById = (state) => _scopedState(state).winners.winnersById;

const _winnersByDrawId = (state) => _scopedState(state).winners.winnersByDrawId;

const _winnerHistorysById = (state) => _scopedState(state).winnerHistory.winnerHistorysById;

const _activeWinners = createSelector(_winnersById, (winners) => {
  return _.reduce(
    winners,
    (accWinners, winner) => {
      if (winner.archived === false) accWinners[winner.id] = winner;
      return accWinners;
    },
    {}
  );
});

// TODO change this selector name. it returns the list of draws in winnersByDrawId that have populated draws
const _populatedDraws = createSelector(_winnersByDrawId, (winnersByDrawId) => {
  return _.reduce(
    winnersByDrawId,
    (accDraws, draw, drawId) => {
      if (!_.isEmpty(draw)) accDraws[drawId] = draw;
      return accDraws;
    },
    {}
  );
});

// get the draws that have winner places
const _populdatedDrawsById = createSelector(
  [_drawsById, _populatedDraws, _activeWinners],
  (drawsById, populatedDraws, activeWinners) => {
    return _.reduce(
      populatedDraws,
      (accDraws, winnerIds, drawId) => {
        accDraws[drawId] = drawsById[drawId];

        const _validWinnersByDraw = _.reduce(
          winnerIds,
          (accWinners, winnerId) => {
            const winner = activeWinners[winnerId];
            if (!_.isEmpty(winner)) accWinners[winnerId] = winner;
            return accWinners;
          },
          {}
        );

        accDraws[drawId] = accDraws[drawId].set('places', _.values(_validWinnersByDraw));
        return accDraws;
      },
      {}
    );
  }
);

// *** WINNER SELECTORS *** /
const hasFetched = (state) => _scopedState(state).winners.hasFetched;
const isFetching = (state) => _scopedState(state).winners.isFetching;

const winners = createSelector(_activeWinners, (winners) => {
  return _.values(winners);
});

const drawIdByWinnerId = createSelector([_winnersByDrawId, (state, winnerId) => winnerId], (draws, winnerId) => {
  let _drawId;
  _.forEach(draws, (winners, drawId) => {
    if (_.includes(winners, winnerId)) {
      _drawId = drawId;
      return false; // break out early if we've found the drawId
    }
  });
  return _drawId;
});

const draws = createSelector(_populdatedDrawsById, (populatedDraws) => _.values(populatedDraws));

const drawsByDate = createSelector([draws, ExperienceSelectors.experience], (draws, activeExperience) => {
  if (activeExperience.isMicroExperience) return _.sortBy(draws, 'dateCreated');
  //macro has a more complex set of ordering params
  else return _.orderBy(draws, ['isManualDraw', 'dateCreated'], ['desc', 'desc']);
});

const drawHasInvalidatedWinners = createSelector([draws, (state, draw) => draw], (draws, draw) => {
  return _.some(draw.places, (place) => {
    return place.isInvalidated;
  });
});

const winnersCount = createSelector(_activeWinners, (winners) => _.values(winners).length);

// *** WINNER HISTORY SELECTORS *** //
const winnerHistorys = createSelector([_winnerHistorysById, _winnersById], (winnerHistorys, winners) => {
  const _history = _.reduce(
    winnerHistorys,
    (accHistory, event, historyId) => {
      accHistory[historyId] = event;

      const _winners = _.reduce(
        accHistory[historyId].winners,
        (accWinners, winnerId) => {
          const _w = _.find(winners, (w) => w.id === winnerId);
          accWinners.push(_w);
          return accWinners;
        },
        []
      );

      accHistory[historyId] = accHistory[historyId].set('winners', _winners);
      return accHistory;
    },
    {}
  );

  return _.take(_.orderBy(_.values(_history), 'eventDate', 'desc'), 5);
});

const isWinnerInManualDraw = createSelector(
  [drawIdByWinnerId, _drawsById],
  (drawId, draws) => draws[drawId].isManualDraw
);

// *** APP SELECTORS *** //
const layout = (state) => _scopedState(state).app.layout;

export default {
  drawHasInvalidatedWinners,
  drawIdByWinnerId,
  draws,
  drawsByDate,
  hasFetched,
  isFetching,
  isWinnerInManualDraw,
  layout,
  winnerHistorys,
  winners,
  winnersCount,
};
